import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import cn from 'classnames';
import first from 'lodash/first';
import last from 'lodash/last';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

import { moveTodo, nestByTabClick } from 'Shared/actions/todos';
import ActionItem from 'Shared/components/ActionItem/ActionItem';
import DiscussionTopic from 'Shared/components/DiscussionTopic/DiscussionTopic';
import { ItemType } from 'Shared/constants/types';
import { createItemFromSourceItemAction } from 'Shared/actions/meetingItems';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { useHandleFocusWorkspaceItem } from 'Dashboard/components/WorkspacePage/hooks/use-handle-focus-workspace-item';

import './WorkspaceItemsList.scss';

const ACTION_ITEMS_SELECTOR = 'workspace.meeting_items';
const DISCUSSION_TOPICS_SELECTOR = 'workspace.discussion_topics';

type Props = {
  items: Array<{
    uuid: string;
    parentUuid: string;
    isHighPriority: boolean;
    isSection: boolean;
  }>;
  forbidDrag?: boolean;
  listOfParentUuid?: string;
  parentIsSection?: boolean;
  listType: string;
  onNestByDrag: Function;
  onArrowUpClick: Function;
  onArrowDownClick: Function;
  customActionsArea?: Function;
  isCompleteToggleDisabled?: boolean;
};

function WorkspaceItemsList({
  items,
  forbidDrag = false,
  listOfParentUuid = null,
  parentIsSection = false,
  listType,
  onNestByDrag,
  onArrowUpClick,
  onArrowDownClick,
  customActionsArea,
  isCompleteToggleDisabled,
}: Props) {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const workspaceId = useSelector(state => state.workspace.id);
  const { itemUuid } = useParams();

  const isDetailsPanelOpen = pathname.includes('/item/') && pathname.includes('/comments');
  const filteredItems = items.filter(({ parentUuid }) => parentUuid === listOfParentUuid);
  const itemsWithoutHighPriority = filteredItems.filter(({ isHighPriority }) => !isHighPriority);
  const hasHighPriorityItems = Boolean(filteredItems.length - itemsWithoutHighPriority.length);
  const firstTodoUuid = itemsWithoutHighPriority.length ? first(itemsWithoutHighPriority).uuid : null;
  const lastTodoUuid = itemsWithoutHighPriority.length ? last(itemsWithoutHighPriority).uuid : null;

  const nestItemByTabClick = useCallback(
    uuid => {
      dispatch(
        nestByTabClick({
          uuid,
          itemType: listType,
          workspaceId,
        })
      );
    },
    [listType, dispatch, workspaceId]
  );

  const handleTodoDrop = useCallback(
    ({ draggedItemUuids, hoveredItemUuid, hoverOffset }) =>
      dispatch(moveTodo(listType, { draggedItemUuids, hoveredItemUuid: hoveredItemUuid || lastTodoUuid, hoverOffset })),
    [listType, lastTodoUuid, dispatch]
  );

  const handleFocusWorkspaceItem = useHandleFocusWorkspaceItem({ workspaceId });

  const handleCreateItemOnEnter = useCallback(
    sourceItemUuid => {
      const newUuid = uuidv4();
      const dueBy = moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(12, 'hours').unix();
      dispatch(
        createItemFromSourceItemAction({ sourceItemUuid, itemUuid: newUuid, positionField: 'position', dueBy })
      ).then(() => {
        dispatch(
          moveTodo(listType, {
            draggedItemUuids: [newUuid],
            hoveredItemUuid: sourceItemUuid,
            hoverOffset: 1,
          })
        );
      });
      dispatch(updateSelectedUuids(newUuid, { isOneSelected: true }));
      handleFocusWorkspaceItem(newUuid);
    },
    [dispatch, handleFocusWorkspaceItem, listType]
  );

  const ItemComponent = listType === ItemType.actionItem ? ActionItem : DiscussionTopic;

  return (
    <div
      className={cn('WorkspaceItemsList d-flex flex-column w-100', {
        'top-level': !listOfParentUuid && listType !== ItemType.discussionTopic,
        'action-item-list': listType === ItemType.actionItem,
        'discussion-topic-list': listType === ItemType.discussionTopic,
      })}
    >
      {filteredItems.map(({ uuid, isSection }) => (
        <ItemComponent
          key={uuid}
          enableHideAnimation
          useNestingDnd
          uuid={uuid}
          isSection={isSection}
          forbidDrag={forbidDrag}
          parentIsSection={parentIsSection}
          nestItemByTabClick={nestItemByTabClick}
          onArrowUpClick={onArrowUpClick}
          onAddItem={handleCreateItemOnEnter}
          onDrop={handleTodoDrop}
          onNestByDrag={onNestByDrag}
          onArrowDownClick={onArrowDownClick}
          withDueDateSection
          withOwnerSection
          selector={listType === ItemType.actionItem ? ACTION_ITEMS_SELECTOR : DISCUSSION_TOPICS_SELECTOR}
          showPresenceComponent
          isFocused={Boolean(itemUuid && itemUuid === uuid) && !pathname.includes('/comments')}
          isFirst={firstTodoUuid === uuid}
          hasHighPriorityItems={!forbidDrag && hasHighPriorityItems}
          onFocusedTodoUpdate={handleFocusWorkspaceItem}
          isLast={uuid === lastTodoUuid}
          enableSelection={!isDetailsPanelOpen}
          customActionsArea={customActionsArea}
          isCompleteToggleDisabled={isCompleteToggleDisabled}
        >
          {items.some(t => t.parentUuid === uuid) ? (
            <WorkspaceItemsList
              listOfParentUuid={uuid}
              forbidDrag={forbidDrag}
              items={items}
              listType={listType}
              parentIsSection={isSection && listType === ItemType.discussionTopic}
              onNestByDrag={onNestByDrag}
              onArrowUpClick={onArrowUpClick}
              onArrowDownClick={onArrowDownClick}
              customActionsArea={customActionsArea}
              isCompleteToggleDisabled={isCompleteToggleDisabled}
            />
          ) : null}
        </ItemComponent>
      ))}
    </div>
  );
}

export default React.memo(WorkspaceItemsList);
